import { Injectable, Inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  updateTitle(title: string): void {
    this.document.title = title;
  }

  updateMetaDescription(description: string): void {
    this.updateMetaTag('description', description);
  }

  updateMetaKeywords(keywords: string): void {
    this.updateMetaTag('keywords', keywords);
  }

  updateOpenGraphTags(data: {
    title?: string;
    description?: string;
    url?: string;
    type?: string;
    image?: string;
  }): void {
    if (data.title) {
      this.updateMetaTag('og:title', data.title, 'property');
    }
    if (data.description) {
      this.updateMetaTag('og:description', data.description, 'property');
    }
    if (data.url) {
      this.updateMetaTag('og:url', data.url, 'property');
    }
    if (data.type) {
      this.updateMetaTag('og:type', data.type, 'property');
    }
    if (data.image) {
      this.updateMetaTag('og:image', data.image, 'property');
    }
  }

  updateTwitterCardTags(data: {
    title?: string;
    description?: string;
    image?: string;
  }): void {
    if (data.title) {
      this.updateMetaTag('twitter:title', data.title);
    }
    if (data.description) {
      this.updateMetaTag('twitter:description', data.description);
    }
    if (data.image) {
      this.updateMetaTag('twitter:image', data.image);
    }
  }

  updateCanonicalUrl(url: string): void {
    const baseUrl = 'https://matools.org';
    
    // Normalize URL: remove trailing slash (except for root), remove query params and hash
    let normalizedUrl = url.split('?')[0].split('#')[0];
    if (normalizedUrl !== '/' && normalizedUrl.endsWith('/')) {
      normalizedUrl = normalizedUrl.slice(0, -1);
    }
    
    const canonicalUrl = normalizedUrl === '/' ? baseUrl : `${baseUrl}${normalizedUrl}`;
    
    let canonicalTag = this.document.querySelector('link[rel="canonical"]');
    
    if (canonicalTag) {
      canonicalTag.setAttribute('href', canonicalUrl);
    } else {
      canonicalTag = this.document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      canonicalTag.setAttribute('href', canonicalUrl);
      this.document.head.appendChild(canonicalTag);
    }
  }

  private updateMetaTag(name: string, content: string, attribute: string = 'name'): void {
    let metaTag = this.document.querySelector(`meta[${attribute}="${name}"]`);
    
    if (metaTag) {
      metaTag.setAttribute('content', content);
    } else {
      metaTag = this.document.createElement('meta');
      metaTag.setAttribute(attribute, name);
      metaTag.setAttribute('content', content);
      this.document.head.appendChild(metaTag);
    }
  }

  // Predefined SEO data for each tool
  getToolSeoData(toolName: string): {
    title: string;
    description: string;
    keywords: string;
    ogData: any;
    twitterData: any;
  } {
    const baseUrl = 'https://matools.org';
    const siteName = 'maTools - Modern Angular Utility Tools';
    
    const seoData: { [key: string]: any } = {
      '': {
        title: 'maTools - Free Online Utility Tools | Math, Text, QR Code & More',
        description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, create rent receipts, and more. All tools are free and easy to use.',
        keywords: 'free online tools, utility tools, QR code generator, EMI calculator, word counter, list compare, UUID generator, rent receipt generator, JSON viewer, text tools, math tools',
        ogData: {
          title: 'maTools - Free Online Utility Tools',
          description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, and more.',
          url: baseUrl,
          type: 'website'
        },
        twitterData: {
          title: 'maTools - Free Online Utility Tools',
          description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, and more.'
        }
      },
      'math-buzz': {
        title: 'Math Buzz - Concentration Game | maTools',
        description: 'Boost your concentration and mental focus with engaging math buzz games and puzzles.',
        keywords: 'math buzz, Math buzz game, puzzle game, math game, concentration game, math practice, math game for kids, math game for adults, math game for students, math game for professionals',
        ogData: {
          title: 'Math Buzz - Concentration Game',
          description: 'Boost your concentration and mental focus with engaging math buzz games and puzzles.',
          url: `${baseUrl}/math-buzz`,
          type: 'website'
        },
        twitterData: {
          title: 'Math Buzz - Concentration Game',
          description: 'Boost your concentration and mental focus with engaging math buzz games and puzzles.',
        }
      },
      'compare-list': {
        title: 'List Compare - Compare Two Lists | maTools',
        description: 'Compare two lists and find differences, similarities, and relationships between them. Advanced list analysis tool.',
        keywords: 'list compare, compare lists, list differences, list analysis, data comparison',
        ogData: {
          title: 'List Compare - Compare Two Lists',
          description: 'Compare two lists and find differences, similarities, and relationships between them.',
          url: `${baseUrl}/compare-list`,
          type: 'website'
        },
        twitterData: {
          title: 'List Compare - Compare Two Lists',
          description: 'Compare two lists and find differences, similarities, and relationships between them.'
        }
      },
      'word-count': {
        title: 'Word Count - Text Analysis Tool | maTools',
        description: 'Analyze text with advanced word counting, character counting, and text statistics. Perfect for writers and content creators.',
        keywords: 'word count, text analysis, character count, text statistics, writing tools',
        ogData: {
          title: 'Word Count - Text Analysis Tool',
          description: 'Analyze text with advanced word counting, character counting, and text statistics.',
          url: `${baseUrl}/word-count`,
          type: 'website'
        },
        twitterData: {
          title: 'Word Count - Text Analysis Tool',
          description: 'Analyze text with advanced word counting, character counting, and text statistics.'
        }
      },
      'generate-uuid': {
        title: 'UUID Generator - Generate Unique Identifiers | maTools',
        description: 'Generate various types of UUIDs (UUID1, UUID4, UUID5) for your applications. Fast and secure UUID generation.',
        keywords: 'UUID generator, unique identifier, UUID1, UUID4, UUID5, GUID generator',
        ogData: {
          title: 'UUID Generator - Generate Unique Identifiers',
          description: 'Generate various types of UUIDs (UUID1, UUID4, UUID5) for your applications.',
          url: `${baseUrl}/generate-uuid`,
          type: 'website'
        },
        twitterData: {
          title: 'UUID Generator - Generate Unique Identifiers',
          description: 'Generate various types of UUIDs (UUID1, UUID4, UUID5) for your applications.'
        }
      },
      'generate-qr-code': {
        title: 'QR Code Generator - Create QR Codes | maTools',
        description: 'Generate QR codes for URLs, text, contact info, and more. Single and batch QR code generation with customization options.',
        keywords: 'QR code generator, QR codes, barcode generator, QR code creator, batch QR codes',
        ogData: {
          title: 'QR Code Generator - Create QR Codes',
          description: 'Generate QR codes for URLs, text, contact info, and more. Single and batch QR code generation.',
          url: `${baseUrl}/generate-qr-code`,
          type: 'website'
        },
        twitterData: {
          title: 'QR Code Generator - Create QR Codes',
          description: 'Generate QR codes for URLs, text, contact info, and more. Single and batch QR code generation.'
        }
      },
      'emi-calculator': {
        title: 'EMI Calculator - Home Loan Calculator | maTools',
        description: 'Calculate EMI for home loans, personal loans, and other loans. Indian currency formatting with detailed breakdown.',
        keywords: 'EMI calculator, home loan calculator, loan EMI, personal loan calculator, Indian currency',
        ogData: {
          title: 'EMI Calculator - Home Loan Calculator',
          description: 'Calculate EMI for home loans, personal loans, and other loans with Indian currency formatting.',
          url: `${baseUrl}/emi-calculator`,
          type: 'website'
        },
        twitterData: {
          title: 'EMI Calculator - Home Loan Calculator',
          description: 'Calculate EMI for home loans, personal loans, and other loans with Indian currency formatting.'
        }
      },
      'rent-receipt': {
        title: 'Rent Receipt Generator - Create Professional Receipts | maTools',
        description: 'Generate professional rent receipts with customizable templates. Perfect for landlords and tenants.',
        keywords: 'rent receipt generator, rent receipt, receipt generator, landlord tools, tenant receipts',
        ogData: {
          title: 'Rent Receipt Generator - Create Professional Receipts',
          description: 'Generate professional rent receipts with customizable templates. Perfect for landlords and tenants.',
          url: `${baseUrl}/rent-receipt`,
          type: 'website'
        },
        twitterData: {
          title: 'Rent Receipt Generator - Create Professional Receipts',
          description: 'Generate professional rent receipts with customizable templates. Perfect for landlords and tenants.'
        }
      },
      'json-viewer': {
        title: 'JSON Viewer - Pretty Print JSON | maTools',
        description: 'Pretty print, validate, and format JSON data. Perfect for developers and data analysts.',
        keywords: 'JSON viewer, JSON formatter, JSON validator, pretty print JSON, JSON tools',
        ogData: {
          title: 'JSON Viewer - Pretty Print JSON',
          description: 'Pretty print, validate, and format JSON data. Perfect for developers and data analysts.',
          url: `${baseUrl}/json-viewer`,
          type: 'website'
        },
        twitterData: {
          title: 'JSON Viewer - Pretty Print JSON',
          description: 'Pretty print, validate, and format JSON data. Perfect for developers and data analysts.'
        }
      },
      'sort': {
        title: 'Sort Tool - Sort and Organize Text | maTools',
        description: 'Sort text data alphabetically, numerically, or by custom criteria. Perfect for organizing lists and data.',
        keywords: 'sort tool, text sorting, alphabetize, organize text, data sorting',
        ogData: {
          title: 'Sort Tool - Sort and Organize Text',
          description: 'Sort text data alphabetically, numerically, or by custom criteria. Perfect for organizing lists.',
          url: `${baseUrl}/sort`,
          type: 'website'
        },
        twitterData: {
          title: 'Sort Tool - Sort and Organize Text',
          description: 'Sort text data alphabetically, numerically, or by custom criteria. Perfect for organizing lists.'
        }
      },
      'board': {
        title: 'White Board | maTools',
        description: 'Free online whiteboard for drawing, sketching, and note-taking. Features multiple drawing tools including pencil, pen, brush, and eraser. Customizable colors, sizes, and favorite color palette. Perfect for brainstorming, teaching, and creative work.',
        keywords: 'white board, online whiteboard, drawing board, sketch board, digital whiteboard, drawing tools, online drawing, free whiteboard, virtual whiteboard, collaborative drawing',
        ogData: {
          title: 'White Board | maTools',
          description: 'Free online whiteboard for drawing, sketching, and note-taking. Features multiple drawing tools including pencil, pen, brush, and eraser.',
          url: `${baseUrl}/board`,
          type: 'website'
        },
        twitterData: {
          title: 'White Board | maTools',
          description: 'Free online whiteboard for drawing, sketching, and note-taking. Features multiple drawing tools including pencil, pen, brush, and eraser.'
        }
      },
      'developer': {
        title: 'Developer | maTools',
        description:
          'Sharanabasu Angadi — Java, Spring Boot, Node.js, Android, Angular, AWS. Actively seeking a role. Creator of maTools.org. Contact: sharan@matools.org.',
        keywords:
          'Sharanabasu Angadi, Java developer, Spring Boot, Node.js, Android Kotlin, Angular, AWS, job seeker, hiring, maTools, open source, chess, report-lib, Maven Central',
        ogData: {
          title: 'Developer | maTools',
          description:
            'Software developer: Java, Spring Boot, Node.js, Android, Angular, AWS. Actively looking for a role. Builds maTools.org and open source.',
          url: `${baseUrl}/developer`,
          type: 'website'
        },
        twitterData: {
          title: 'Developer | maTools',
          description:
            'Java, Spring Boot, Node.js, Android, Angular, AWS. Actively seeking a role. Creator of maTools.org.'
        }
      }
    };

    return seoData[toolName] || {
      title: 'maTools - Free Online Utility Tools | Math, Text, QR Code & More',
      description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, create rent receipts, and more. All tools are free and easy to use.',
      keywords: 'free online tools, utility tools, QR code generator, EMI calculator, word counter, list compare, UUID generator, rent receipt generator, JSON viewer, text tools, math tools',
      ogData: {
        title: 'maTools - Free Online Utility Tools',
        description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, and more.',
        url: baseUrl,
        type: 'website'
      },
      twitterData: {
        title: 'maTools - Free Online Utility Tools',
        description: 'Free online utility tools for everyday tasks. Generate QR codes, calculate EMI, compare lists, count words, generate UUIDs, and more.'
      }
    };
  }
}
